import { Button, Flex, RadioGroupItem } from "@suns/design-system";
import { FilterValues } from "../all-reports-listings";
import { X } from "lucide-react";
import { Filter } from "@suns/design-system/src/components/Filter/Filter";
import { PlayerFilter, TeamFilter } from "@/components";
import { PlayerLevel } from "@suns/api";
import { useAccount } from "@/shared/hooks";
import { PersonnelFilter } from "@/components/PersonnelFilter/PersonnelFilter";
import { AgentFilter } from "@/components/AgentFilter/AgentFilter";
import { RadioGroup } from "@radix-ui/react-radio-group";

interface ReportFiltersProps {
  onChange: (filterValues: FilterValues) => void;
  onClear: () => void;
  type: "intel" | "reports";
  filterValues: FilterValues;
  authors?: {
    key: string;
    name: string;
  }[];
  authorsLoading?: boolean;
}

export default function ReportFilters({
  onChange,
  onClear,
  type,
  filterValues,
  authors,
  authorsLoading,
}: ReportFiltersProps) {
  const { hasAdminAccess } = useAccount();

  function onRadioValueChange(value: string) {
    const level = value == "ALL" ? undefined : (value as PlayerLevel);

    onChange({
      ...filterValues,
      level,
    });
  }

  return (
    <Flex gap="sm" align="center" wrap={true} className="w-full">
      <RadioGroup
        onValueChange={onRadioValueChange}
        value={filterValues.level ?? "ALL"}
      >
        <Flex gap="md">
          {["All", "Pro", "Amateur"].map((level) => (
            <RadioGroupItem
              key={level}
              label={level}
              value={level.toUpperCase()}
            />
          ))}
        </Flex>
      </RadioGroup>

      <PlayerFilter
        value={filterValues.playerIds?.map(String) || []}
        onChange={(selectedKeys: string[]) => {
          onChange({ ...filterValues, playerIds: selectedKeys.map(Number) });
        }}
      />

      <TeamFilter
        value={filterValues.teamIds?.map(String) || []}
        onChange={(selectedKeys: string[]) => {
          onChange({ ...filterValues, teamIds: selectedKeys.map(Number) });
        }}
      />

      {hasAdminAccess && type == "intel" ? (
        <AgentFilter
          value={
            filterValues.agentIds?.map((id: number) => id.toString()) || []
          }
          onChange={(selectedKeys: string[]) => {
            onChange({ ...filterValues, agentIds: selectedKeys.map(Number) });
          }}
        />
      ) : null}

      {hasAdminAccess && type == "intel" ? (
        <PersonnelFilter
          value={
            filterValues.personnelIds?.map((id: number) => id.toString()) || []
          }
          onChange={(selectedKeys: string[]) => {
            onChange({
              ...filterValues,
              personnelIds: selectedKeys.map(Number),
            });
          }}
        />
      ) : null}

      {authors && (
        <Filter
          avoidCollisions={false}
          title={authorsLoading ? "Loading..." : "Author"}
          value={filterValues.authorUsername || []}
          onChange={(value) =>
            onChange({
              ...filterValues,
              authorUsername: value as string[],
            })
          }
          options={authors?.map((author) => ({
            key: author.key,
            name: author.name,
          }))}
        />
      )}
      <Button variant="ghost" onClick={onClear}>
        <Flex gap="xs" align="center" justify="center">
          Reset
          <X className="w-4 pt-0.5" />
        </Flex>
      </Button>
    </Flex>
  );
}
